import { Avatar, Box, Button, Card, CardActions, CardContent, Fab, IconButton, Typography } from "@mui/material";
import { ConnectionName, ConnectionStatus, mapConnectionUIName } from "@arelia-types/connection";
import { openAuthWindow } from "../helpers";
import { ArelTooltip } from "@arelia-components/ui-atoms/Tooltip";
import { LinkOutlined } from "@mui/icons-material";
import { formatDate } from "@arelia-utils/helpers/dates";
import CloseIcon from '@mui/icons-material/Close';
import { useContext } from "react";
import ConnectionsContext from "../ConnectionsContext";

type Props = {
  name: ConnectionName,
  icon: string,
  iconRadius?: string,
  connected: boolean,
  unauthorized: boolean,
  updatedAt?: Date,
  status: string,
  modeLabel?: string,
  directConnection?: boolean,
  renderConnectedStatus?: () => "" | JSX.Element,
  onConnect: () => void
}

export default function ConnectionCard({
  name,
  icon,
  iconRadius = "50%",
  connected,
  unauthorized,
  updatedAt,
  status,
  modeLabel,
  directConnection = false,
  renderConnectedStatus,
  onConnect
}: Props) {
  const isConnectionDisabled = status === ConnectionStatus.notSupported;
  const { activeConnections, setActiveConnections } = useContext(ConnectionsContext);

  const handleConnect = () => {
    if (!connected && directConnection) openAuthWindow(name);
    else onConnect && onConnect();
  }

  const removeConnection = (name: ConnectionName) => {
    setActiveConnections(activeConnections.filter(c => c !== name));
  }

  return (
    <Card sx={{ minHeight: "310px", height: "100%", position: "relative" }}>
      {name !== ConnectionName.arelia && !connected && <IconButton
        sx={{ position: "absolute", top: 5, right: 5, zIndex: 2, color: "#818C9B"}}
        color="inherit"
        onClick={() => removeConnection(name)}
      >
        <CloseIcon />
      </IconButton>}
      <CardContent sx={{ alignItems: "center", justifyContent: "flex-start", padding: "0" }}>
        <Avatar src={icon} {...styles.avatar(iconRadius)} />
        <Typography variant="subtitle2" {...styles.name}>
          { mapConnectionUIName(name) }
        </Typography>
        <Box textAlign="center">
          <Typography variant="subtitle1" {...styles.status(connected, unauthorized)}>{status}</Typography>
          {connected && <>
            <Typography variant="body2" fontStyle="italic">
              {modeLabel
                ? modeLabel
                : updatedAt
                  ? <>Last updated on: {formatDate(updatedAt, "MM/dd/yy")}</>
                  : "Realtime"
              }
            </Typography>
            {renderConnectedStatus && renderConnectedStatus()}
          </>}
        </Box>
      </CardContent>
      <CardActions sx={{ justifyContent: "center", pb: "20px" }}>
        {  !connected ?
        <Button
          variant="contained"
          sx={{ width: 135, color: "#fff", textTransform: "initial" }}
          onClick={handleConnect}
          disabled={isConnectionDisabled}
        >
          Connect
        </Button>
        :
          <Box display="flex">
            <ArelTooltip title={"View/Edit Connection"}>
              <Fab
                size="small"
                sx={{ mr: 1, ...styles.fabStyles }}
                onClick={handleConnect}
                disabled={isConnectionDisabled}
              >
                <LinkOutlined />
              </Fab>
            </ArelTooltip>
          </Box>
        }
      </CardActions>
    </Card>
  );
}

const styles = {
  avatar: (iconRadius: any) => ({
    sx: {
      borderRadius: iconRadius,
      width: 60,
      height: 60,
      m: "20px"
    },
    imgProps: { sx: { objectFit: "contain" } }
  }),
  name: {
    sx: { fontWeight: 600, mb: "5px" }
  },
  fabStyles: {
    bgcolor: "common.white",
    "&:hover": {
      bgcolor: "common.white",
      "& svg": {
        color: "primary.main",
      },
    }
  },
  status: (connected: boolean, unauthorized: boolean) => ({
    color: connected ? (unauthorized ? "error.main" : "success.main") : "secondary",
    mb: "15px"
  })
}
